import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signIn: contextSignIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Get the redirect URL from state or default to dashboard
  const from = (location.state as any)?.from?.pathname || '/dashboard';

  useEffect(() => {
    // If already authenticated, redirect
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      // Use the AuthContext signIn method which will update the user state
      await contextSignIn({ username: email, password });
      // Don't navigate here - let the useEffect handle it when user state updates
    } catch (err: any) {
      if (err.code === 'UserNotConfirmedException') {
        setError('Please verify your email before signing in.');
      } else if (err.code === 'NotAuthorizedException') {
        setError('Invalid email or password.');
      } else if (err.code === 'UserNotFoundException') {
        setError('No account found with this email.');
      } else if (err.message && err.message.includes('already a signed in user')) {
        // User is already signed in - navigate immediately
        navigate(from, { replace: true });
        return;
      } else {
        setError(err.message || 'An error occurred during sign in.');
      }
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <div style={{ 
        background: 'white',
        borderRadius: 16,
        padding: 48,
        maxWidth: 480,
        width: '100%',
        margin: '0 16px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <h1 style={{ textAlign: 'center', marginBottom: 8 }}>Welcome back</h1>
        <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: 32 }}>
          Sign in to access your meal plans and pantry
        </p>

        {message && (
          <div style={{ 
            padding: 12, 
            background: '#efe', 
            border: '1px solid #cfc', 
            borderRadius: 8,
            color: '#0a0',
            fontSize: 14,
            marginBottom: 16
          }}>
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label htmlFor="email" style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 600 }}>
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                fontSize: 16,
              }}
            />
          </div>

          <div>
            <label htmlFor="password" style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 600 }}>
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                fontSize: 16,
              }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
              <input type="checkbox" />
              Remember me
            </label>
            <a href="https://nutrir.ai/forgot-password" style={{ fontSize: 14, color: '#6366f1' }}>
              Forgot password?
            </a>
          </div>

          {error && (
            <div style={{ 
              padding: 12, 
              background: '#fee', 
              border: '1px solid #fcc', 
              borderRadius: 8,
              color: '#c00',
              fontSize: 14
            }}>
              {error}
            </div>
          )}

          <button 
            type="submit" 
            style={{ 
              marginTop: 16, 
              width: '100%',
              padding: '12px',
              background: '#6366f1',
              color: 'white',
              border: 'none',
              borderRadius: 10,
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer'
            }}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <div style={{ marginTop: 32, textAlign: 'center', paddingTop: 32, borderTop: '1px solid #e5e7eb' }}>
          <p style={{ color: '#6b7280', marginBottom: 12 }}>
            Don't have an account?
          </p>
          <a href="https://nutrir.ai/signup" style={{ color: '#6366f1', textDecoration: 'none' }}>
            Create account
          </a>
        </div>
      </div>
    </div>
  );
}
